import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { AUDIENCE_PRESETS, describeAudience } from "@/lib/surveys/audience-presets";
import type { SurveyAudience } from "@/lib/surveys/types";
import { Card, Field, inputCls } from "./form";
import { adminFetch } from "@/api";

interface Props {
  audience: SurveyAudience;
  readOnly: boolean;
  surveyId?: string;
  cohortFrozenAt?: string | null;
  onChange: (a: SurveyAudience) => void;
}

/** A preset is "on" when every field it sets matches the current audience. */
function presetOn(a: SurveyAudience, p: SurveyAudience): boolean {
  const entries = Object.entries(p);
  if (entries.length === 0) return Object.keys(a).length === 0;
  return entries.every(([k, v]) => JSON.stringify(a[k as keyof SurveyAudience]) === JSON.stringify(v));
}

function clean(a: SurveyAudience): SurveyAudience {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(a)) {
    if (v === undefined || v === "" || (Array.isArray(v) && v.length === 0) || (k === "group" && v === "any")) continue;
    out[k] = v;
  }
  return out as SurveyAudience;
}

export function AudienceEditor({ audience, readOnly, surveyId, cohortFrozenAt, onChange }: Props) {
  const [count, setCount] = useState<number | null>(null);
  const [counting, setCounting] = useState(false);
  const [tick, setTick] = useState(0);

  const set = (patch: Partial<SurveyAudience>) => onChange(clean({ ...audience, ...patch }));
  const num = (v: string) => (v === "" ? undefined : Math.max(0, Math.floor(Number(v))));

  useEffect(() => {
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setCounting(true);
      try {
        const res = await adminFetch("/api/surveys/audience-count", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ audience, surveyId, cohortFrozenAt }),
          signal: ctrl.signal,
        });
        const json = await res.json();
        setCount(res.ok ? json.data.count : null);
      } catch {
        // aborted or offline — keep the last number
      } finally {
        setCounting(false);
      }
    }, 500);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [audience, surveyId, cohortFrozenAt, tick]);

  const toggleList = <T extends string>(list: T[] | undefined, v: T): T[] =>
    list?.includes(v) ? list.filter((x) => x !== v) : [...(list ?? []), v];

  return (
    <Card title="Who sees it">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {AUDIENCE_PRESETS.map((p) => {
          const on = presetOn(audience, p.apply);
          return (
            <button
              key={p.id}
              type="button"
              disabled={readOnly}
              onClick={() => onChange(p.id === "everyone" ? {} : clean({ ...audience, ...p.apply }))}
              className={`text-left rounded-xl border px-3 py-2.5 transition-all disabled:opacity-60 ${
                on ? "border-emerald-500/40 bg-emerald-500/[0.06]" : "border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12]"
              }`}
            >
              <div className="text-[12.5px] font-semibold text-white">{p.label}</div>
              <div className="text-[11px] text-zinc-500">{p.description}</div>
            </button>
          );
        })}
      </div>

      <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider pt-2">Filters (all must match)</div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="Active within (days)">
          <input type="number" min={1} className={inputCls} disabled={readOnly} value={audience.activeWithinDays ?? ""} placeholder="any" onChange={(e) => set({ activeWithinDays: num(e.target.value) })} />
        </Field>
        <Field label="Inactive for (days)">
          <input type="number" min={1} className={inputCls} disabled={readOnly} value={audience.inactiveForDays ?? ""} placeholder="any" onChange={(e) => set({ inactiveForDays: num(e.target.value) })} />
        </Field>
        <Field label="Minimum expenses">
          <input type="number" min={1} className={inputCls} disabled={readOnly} value={audience.minExpenses ?? ""} placeholder="any" onChange={(e) => set({ minExpenses: num(e.target.value) })} />
        </Field>
        <Field label="Account age at least (days)">
          <input type="number" min={1} className={inputCls} disabled={readOnly} value={audience.minAccountAgeDays ?? ""} placeholder="any" onChange={(e) => set({ minAccountAgeDays: num(e.target.value) })} />
        </Field>
        <Field label="Account age at most (days)">
          <input type="number" min={1} className={inputCls} disabled={readOnly} value={audience.maxAccountAgeDays ?? ""} placeholder="any" onChange={(e) => set({ maxAccountAgeDays: num(e.target.value) })} />
        </Field>
        <Field label="Group">
          <select className={inputCls} disabled={readOnly} value={audience.group ?? "any"} onChange={(e) => set({ group: e.target.value as SurveyAudience["group"] })}>
            <option value="any" className="bg-[#0b0b0d]">Any</option>
            <option value="shared" className="bg-[#0b0b0d]">In a shared group</option>
            <option value="personal_only" className="bg-[#0b0b0d]">Personal only</option>
          </select>
        </Field>
        <Field label="Minimum app version">
          <input className={inputCls} disabled={readOnly} value={audience.minAppVersion ?? ""} placeholder="e.g. 1.0.5" onChange={(e) => set({ minAppVersion: e.target.value.trim() || undefined })} />
        </Field>
        <Field label="Sample of matching users (%)">
          <input type="number" min={1} max={100} className={inputCls} disabled={readOnly} value={audience.samplePercent ?? ""} placeholder="100" onChange={(e) => set({ samplePercent: num(e.target.value) })} />
        </Field>
        <div className="flex flex-col gap-2">
          <span className="block text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">Platform · language</span>
          <div className="flex flex-wrap gap-3 text-[12.5px] text-zinc-300">
            {(["ios", "android"] as const).map((p) => (
              <label key={p} className="inline-flex items-center gap-1.5">
                <input type="checkbox" className="accent-emerald-500" disabled={readOnly} checked={audience.platforms?.includes(p) ?? false} onChange={() => set({ platforms: toggleList(audience.platforms, p) })} />
                {p === "ios" ? "iOS" : "Android"}
              </label>
            ))}
            {(["en", "hi"] as const).map((l) => (
              <label key={l} className="inline-flex items-center gap-1.5">
                <input type="checkbox" className="accent-emerald-500" disabled={readOnly} checked={audience.languages?.includes(l) ?? false} onChange={() => set({ languages: toggleList(audience.languages, l) })} />
                {l === "en" ? "English" : "Hindi"}
              </label>
            ))}
          </div>
          <span className="text-[11px] text-zinc-600">None ticked = all</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {describeAudience(audience).map((c) => (
          <span key={c} className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-[11.5px]">{c}</span>
        ))}
      </div>

      <div className="flex items-center gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
        <div>
          <div className="text-[11px] text-zinc-500">Users who can see it right now</div>
          <div className="text-[24px] font-bold text-white" data-testid="audience-count">
            {count == null ? "—" : count.toLocaleString("en-IN")}
            {counting && <span className="ml-2 text-[12px] text-zinc-500 font-normal">counting…</span>}
          </div>
        </div>
        <div className="flex-1" />
        <button type="button" onClick={() => setTick((t) => t + 1)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/[0.06] text-[12px] font-semibold text-zinc-300 hover:text-white">
          <RefreshCw className="w-3.5 h-3.5" /> Recount
        </button>
      </div>
      <p className="text-[11px] text-zinc-600">
        Only users who signed up before publishing are included — new sign-ups don&apos;t get a live survey.
        {!surveyId && audience.samplePercent ? " The sample is an estimate until the survey is saved." : ""}
      </p>
    </Card>
  );
}
